import { FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, FileText, Play, Plus } from "lucide-react";
import { api, type Hotel, type ReportSchedule } from "../api/client";

export function ReportsPage() {
  const queryClient = useQueryClient();
  const schedules = useQuery({ queryKey: ["report-schedules"], queryFn: () => api<ReportSchedule[]>("/api/admin/report-schedules") });
  const hotels = useQuery({ queryKey: ["hotels"], queryFn: () => api<Hotel[]>("/api/admin/hotels") });

  const createSchedule = useMutation({
    mutationFn: (body: unknown) => api<ReportSchedule>("/api/admin/report-schedules", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["report-schedules"] })
  });

  const updateSchedule = useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) => api<ReportSchedule>(`/api/admin/report-schedules/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["report-schedules"] })
  });

  const runNow = useMutation({
    mutationFn: (scheduleId: string) => api<{ queued: boolean; jobId: string }>("/api/admin/reports/run-now", {
      method: "POST",
      body: JSON.stringify({ scheduleId })
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["report-schedules"] })
  });

  function submitSchedule(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const recipients = String(form.get("recipients") ?? "")
      .split(",")
      .map((email) => email.trim())
      .filter(Boolean);
    createSchedule.mutate({
      hotelId: String(form.get("hotelId") || "") || undefined,
      frequency: String(form.get("frequency")),
      recipients,
      active: true,
      filters: {},
      nextRunAt: String(form.get("nextRunAt") || "") ? new Date(String(form.get("nextRunAt"))).toISOString() : undefined
    });
    event.currentTarget.reset();
  }

  return (
    <div className="page-stack">
      <section className="tool-hero card reports-hero">
        <div>
          <span className="panel-eyebrow left">PDF Reports</span>
          <h2>Automatski i rucni izvjestaji za menadzment.</h2>
          <p>Worker generise PDF sa KPI summary, kriticnim lokacijama i rule-based akcijama, zatim ga salje preko SMTP adaptera.</p>
        </div>
        <div className="object-brand-card">
          <FileText size={34} />
          <strong>{schedules.data?.filter((schedule) => schedule.active).length ?? 0} aktivno</strong>
          <span>weekly/monthly PDF schedules</span>
        </div>
      </section>

      <div className="grid-two narrow">
        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>Novi raspored</h2>
              <p>Primaoci se unose kao comma-separated email lista. Hotel prazan znaci report za cijelu organizaciju.</p>
            </div>
          </div>
          <form className="compact-form" onSubmit={submitSchedule}>
            <h3><Plus size={16} /> Schedule</h3>
            <select name="hotelId">
              <option value="">Cijela organizacija</option>
              {(hotels.data ?? []).map((hotel) => <option key={hotel.id} value={hotel.id}>{hotel.name}</option>)}
            </select>
            <select name="frequency" defaultValue="weekly">
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
            <input name="nextRunAt" type="datetime-local" />
            <input name="recipients" type="text" placeholder="gm@hotel.test, ops@hotel.test" required />
            <button type="submit"><CalendarClock size={16} /> Sacuvaj raspored</button>
          </form>
        </section>

        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>Report schedules</h2>
              <p>Run now stavlja job u Redis queue; PDF se salje kada worker obradi job.</p>
            </div>
          </div>
          <div className="rule-list">
            {(schedules.data ?? []).map((schedule) => (
              <article className="rule-card" key={schedule.id}>
                <div>
                  <strong>{schedule.hotel?.name ?? "Cijela organizacija"}</strong>
                  <span>{schedule.frequency} - next {formatDate(schedule.nextRunAt)}</span>
                </div>
                <div className="rule-meta">
                  <span>{schedule.active ? "active" : "paused"}</span>
                  <span>{schedule.recipients.length} recipients</span>
                  <span>{schedule.hotel ? "hotel scoped" : "org scoped"}</span>
                  <span>PDF email</span>
                </div>
                <small>{schedule.recipients.join(", ")}</small>
                <div className="row-actions">
                  <button onClick={() => runNow.mutate(schedule.id)} disabled={runNow.isPending}>
                    <Play size={16} /> Run now
                  </button>
                  <button onClick={() => updateSchedule.mutate({ id: schedule.id, body: { active: !schedule.active } })}>
                    {schedule.active ? "Pause" : "Activate"}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function formatDate(value: string) {
  return new Date(value).toLocaleString();
}
